import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Printer } from 'lucide-react';
import StatementSheet from './StatementSheet';
import { normalizeStatementItems, totalsOfStatement, STATEMENT_LEDGER_CSS } from '../utils/statementView';
import { printStatement } from '../utils/printStatement';
import {
    EXPSTMT_DISCLAIMER, EXPSTMT_NOTICE_HEAD, EXPSTMT_SHEET_TITLE, EXPSTMT_BADGE, EXPSTMT_NUMBER_LABEL, EXPSTMT_MAX_ROWS,
    dateFromYmd, expectedPrintTitle,
} from '../services/expectedStatementApi';

// 「예 상 기 공 료 (참고용)」 모달 (2026-10-07 — 기획 docs/기획서_치과_기간예상명세서_2026-10-07.md §3-3·§3-5, 계약 §4, 법무 §3 ☆)
// - StatementPreviewModal 복제 아님: 문서 id 없음 → 재조회·이전/다음·[정산 처리] 없음. StatementSheet 양식만 재사용(title·vatNote prop).
// - Firestore 쓰기 0 · 저장·엑셀 내보내기 없음(결정 6) · 인쇄/PDF 만. 제목·배지·파일명·인쇄본에 "거래명세서" 문자열 0, 계좌·사업자번호 0.
// - 치과(viewer 'clinic')는 서버 getExpectedStatement 응답을, 기공소(viewer 'lab')는 RevenueQuickView 웹 정본 엔진 결과를 같은 모양(data)으로 넘긴다 —
//   표시 규칙(귀속 미확인 대체·리메이크 "확인 필요"·미등록·명세서 포함 배지)은 양쪽 동일(법무 ⑤ 조건).
// - 환자 노출 화면 아님(치과 대표·직원 핀 뒤 B2B) — 홍보·유인 문구 없음(의료법).
//
// props
//   data         계약 §1 응답 모양 { labName, clinicName, range:{from,to,days}, rows[], summary{}, disclaimer }
//   viewer       'clinic' | 'lab' — 헤더 보조 문구만 다름
//   footerExtra  (선택) 하단 추가 버튼(기공소 [단가표 수정] 등)
//   onClose      닫기

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const won = (n) => (Math.round(Number(n) || 0)).toLocaleString('ko-KR');

const badgeStyle = (bg, color, border) => ({
    display: 'inline-block', marginLeft: 5, fontSize: 10, fontWeight: 800, background: bg, color, border: `1px solid ${border}`,
    padding: '1px 7px', borderRadius: 5, verticalAlign: 'middle', whiteSpace: 'nowrap',
});

function ExpectedStatementModal({ data, viewer = 'clinic', footerExtra, onClose }) {
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
    const dialogRef = useRef(null);
    const sheetRef = useRef(null);
    const closeBtnRef = useRef(null);

    // 반응형 (StatementPreviewModal 과 같은 768px 기준)
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // 열린 동안 뒤 화면 스크롤 잠금
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);

    useEffect(() => { closeBtnRef.current?.focus(); }, []);

    // ESC 닫기 · Tab 포커스 트랩
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
        if (e.key === 'Tab' && dialogRef.current) {
            const nodes = Array.from(dialogRef.current.querySelectorAll(FOCUSABLE));
            if (nodes.length === 0) return;
            const first = nodes[0];
            const last = nodes[nodes.length - 1];
            if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
            else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
    }, [onClose]);
    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    const rows = useMemo(() => (Array.isArray(data?.rows) ? data.rows : []), [data]);
    const summary = data?.summary || {};
    const range = data?.range || {};
    const labName = data?.labName || '-';
    const clinicName = data?.clinicName || '-';
    const disclaimer = data?.disclaimer || EXPSTMT_DISCLAIMER;   // 서버 값 그대로 — 없을 때만 사본 폴백

    // 양식용 합성 문서 — supplierInfo 없음 → 계좌·사업자번호 칸이 자연히 비움. 정산·비고·수정이력 필드 없음
    const statement = useMemo(() => ({
        statementNumber: EXPSTMT_NUMBER_LABEL,
        startDate: dateFromYmd(range.from) || null,
        endDate: dateFromYmd(range.to) || null,
        fromUserName: labName,
        toUserName: clinicName,
    }), [range.from, range.to, labName, clinicName]);

    // 계약 §4 행 → 열 매핑. _row 에 원본을 두고 slots 에서 배지·대체 표기를 읽는다
    const items = useMemo(() => normalizeStatementItems(rows.map(r => ({
        createdAt: r.createdAtMs ? new Date(r.createdAtMs) : null,
        dueDate: r.dueDateMs ? new Date(r.dueDateMs) : null,
        patientName: r.patientName || '',
        toothInfo: r.toothInfo || '-',
        toothCount: Number(r.toothCount) || 0,
        unitPrice: Number(r.unitPrice) || 0,
        price: Number(r.amount) || 0,
        totalPrice: Number(r.amount) || 0,
        source: r.source,
        isRemake: !!r.isRemake,
        remakeKind: r.remakeKind || '',
        _row: r,
    }))), [rows]);
    const totals = useMemo(() => totalsOfStatement(statement, items), [statement, items]);

    const handlePrint = () => {
        const ok = printStatement({ root: sheetRef.current, title: expectedPrintTitle(labName, range.from, range.to) });
        if (!ok) alert('인쇄 창을 열지 못했습니다. 잠시 후 다시 시도해 주세요.');
    };

    if (!data) return null;

    const unpricedOnly = Math.max(0, (Number(summary.unpricedCount) || 0) - (Number(summary.remakeCount) || 0));
    const unmatched = Number(summary.unmatchedByName?.count) || 0;

    // ── slots: 행 표기 규칙 (계약 §4 — 치과·기공소 동일) ──
    const slots = {
        hideDisputeHistory: true,
        row: {
            showSourceBadges: false,   // 기본 "자동수집" no-print 배지 대신 아래 배지(인쇄에도 남김)
            patient: (item) => {
                const r = item._row || {};
                const confirmedLabel = r.confirmed
                    ? `명세서 ${r.confirmed.statementNumber ? `${r.confirmed.statementNumber} ` : ''}포함${r.confirmed.count > 1 ? ` 외 ${r.confirmed.count - 1}` : ''}`
                    : '';
                return (
                    <>
                        {r.unattributed
                            ? <span style={{ color: '#64748B', fontStyle: 'italic' }}>{item.patientName || '-'}</span>
                            : (r.orderId
                                ? <button type="button" className="expstmt-link" onClick={() => navigate(`/view-order/${r.orderId}`)}
                                    title="의뢰서 열기"
                                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#1E293B', font: 'inherit', textDecoration: 'underline', textUnderlineOffset: 3 }}>
                                    {item.patientName || '-'}
                                </button>
                                : (item.patientName || '-'))}
                        {r.source === 'auto' && <span style={badgeStyle('#EEF2FF', '#4338CA', '#C7D2FE')}>자동 수집</span>}
                        {confirmedLabel && <span style={badgeStyle('#ECFDF5', '#047857', '#A7F3D0')} title="이미 발행된 거래명세서에 들어간 의뢰입니다. 금액이 다르면 명세서가 맞습니다">{confirmedLabel}</span>}
                    </>
                );
            },
            unitPrice: (item) => {
                const r = item._row || {};
                if (r.isRemake) return <span style={{ color: '#B45309', fontWeight: 700, fontSize: 12 }}>확인 필요</span>;
                if (!r.priced) return <span style={{ color: '#94A3B8', fontSize: 12 }}>미등록</span>;
                return undefined;
            },
        },
        // ☆ C-2 상단 안내 — print-area 안(인쇄에도 나옴). 첫 문장 굵게 + 둘째 문장부터 서버 disclaimer 그대로
        beforeTable: (
            <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12.5, color: '#78350F', lineHeight: 1.65 }}>
                <span style={{ ...badgeStyle('#FEF3C7', '#B45309', '#FCD34D'), marginLeft: 0, marginRight: 8, fontSize: 11 }}>{EXPSTMT_BADGE}</span>
                <b>{EXPSTMT_NOTICE_HEAD}</b>{' '}{disclaimer}
            </div>
        ),
        // 요약 줄 — 합계 아래(print-area 안)
        afterNotes: (
            <div style={{ marginTop: 16, padding: '12px 14px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 12.5, color: '#334155', lineHeight: 1.8 }}>
                <div>
                    <b>예상 합계 {won(summary.amount)}원</b>
                    {' · '}품목 {Number(summary.itemCount) || 0}개 · 치아 {Number(summary.teeth) || 0}개 · 기간 {range.from || '-'} ~ {range.to || '-'}{range.days ? ` (${range.days}일)` : ''}
                </div>
                {unpricedOnly > 0 && <div>단가 미등록 {unpricedOnly}개 — 합계에 빠짐</div>}
                {(Number(summary.remakeCount) || 0) > 0 && <div>리메이크·수리 {summary.remakeCount}건 — 확인 필요</div>}
                {(Number(summary.confirmedCount) || 0) > 0 && (
                    <div>명세서 포함 {summary.confirmedCount}건 · 확정 금액 {won(summary.confirmedAmount)}원 — 명세서와 금액이 다르면 명세서가 맞습니다</div>
                )}
                {(Number(summary.unattributedPatientRows) || 0) > 0 && (
                    <div>자동 수집 건 {summary.unattributedPatientRows}개는 거래처 확인 전이라 환자명을 표시하지 않습니다</div>
                )}
                {unmatched > 0 && <div>거래처 자동 매칭 안 된 {unmatched}건 제외</div>}
                {summary.truncated && <div style={{ color: '#B45309', fontWeight: 700 }}>{EXPSTMT_MAX_ROWS}행까지만 표시 — 기간을 줄여 주세요(합계는 전체 기준)</div>}
            </div>
        ),
    };

    const btnBase = {
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 8,
        fontSize: 13, fontWeight: 600, cursor: 'pointer', border: '1px solid #E2E8F0', background: '#fff', color: '#334155',
    };

    return (
        <div
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
            style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1100,
                background: 'rgba(15, 23, 42, 0.55)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: isMobile ? 0 : 20,
            }}
        >
            <style>{STATEMENT_LEDGER_CSS}</style>
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="expected-statement-title"
                style={{
                    background: '#F8FAFC',
                    width: isMobile ? '100%' : 'min(960px, 94vw)',
                    height: isMobile ? '100%' : 'auto',
                    maxHeight: isMobile ? '100%' : '90vh',
                    borderRadius: isMobile ? 0 : 16,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                    display: 'flex', flexDirection: 'column', overflow: 'hidden',
                }}
            >
                {/* 헤더 */}
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                    padding: isMobile ? '12px 14px' : '14px 20px', background: '#fff', borderBottom: '1px solid #E2E8F0',
                }}>
                    <h3 id="expected-statement-title" style={{ margin: 0, fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#0f172a' }}>
                        예상 기공료
                    </h3>
                    <span style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>
                        {viewer === 'lab' ? `${clinicName} (치과가 보는 화면)` : labName}
                    </span>
                    <span style={{ fontSize: 12, color: '#64748B' }}>{range.from || '-'} ~ {range.to || '-'}</span>
                    <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: '#FEF3C7', color: '#B45309', border: '1px solid #FCD34D' }}>
                        {EXPSTMT_BADGE}
                    </span>
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <button
                            ref={closeBtnRef}
                            onClick={onClose}
                            aria-label="닫기"
                            title="닫기 (ESC)"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#64748B', display: 'inline-flex' }}
                        >
                            <X size={22} />
                        </button>
                    </div>
                </div>

                {/* 본문 (세로 스크롤) */}
                <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto', padding: isMobile ? 10 : 20 }}>
                    {rows.length === 0 && (
                        <div className="no-print" style={{ padding: '8px 12px', background: '#F1F5F9', border: '1px solid #E2E8F0', borderRadius: 8, marginBottom: 12, fontSize: 12.5, color: '#64748B' }}>
                            이 기간에 계산할 의뢰 건이 없습니다.
                        </div>
                    )}
                    <div style={{ minWidth: isMobile ? 640 : undefined }}>
                        <StatementSheet
                            statement={statement}
                            items={items}
                            totals={totals}
                            rootRef={sheetRef}
                            slots={slots}
                            title={EXPSTMT_SHEET_TITLE}
                            vatNote={null}
                        />
                    </div>
                </div>

                {/* 하단 버튼 — 정산 버튼 없음 · 저장·엑셀 없음 */}
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                    padding: isMobile ? '10px 12px' : '12px 20px', background: '#fff', borderTop: '1px solid #E2E8F0',
                }}>
                    {footerExtra}
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button
                            onClick={handlePrint}
                            title="인쇄 창에서 '대상: PDF로 저장'을 고르면 파일로 남습니다"
                            style={btnBase}
                        >
                            <Printer size={16} /> 인쇄 / PDF 저장
                        </button>
                        <button onClick={onClose} style={btnBase}>
                            닫기
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ExpectedStatementModal;
